import { useState, useEffect, useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, History, Loader2, Search, X } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Pagination } from '@/components/shared/Pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useCampaigns } from '@/hooks/useCampaigns';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReviewConfigs } from '@/hooks/useReviewConfigs';
import { useStartReview, useCampaignReview } from '@/hooks/useReviews';
import { formatDate } from '@/utils/format';

import type { CampaignStatus, CampaignQuery } from '@repo/shared-types';

const STATUS_BADGE_STYLES: Record<CampaignStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  FINISHED: 'bg-slate-200 text-slate-800',
  STOPPED: 'bg-rose-100 text-rose-800',
};

const REVIEW_FILTER_OPTIONS = [
  { value: '', label: 'All campaigns' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'not_reviewed', label: 'Not Reviewed' },
];

export const CampaignReviewsPage = () => {
  usePageTitle('Campaign Reviews');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const limit = 10;

  // Read state from URL
  const page = Number(searchParams.get('page')) || 1;
  const reviewFilter = searchParams.get('reviewed') || '';

  // Local state for search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const [reviewingCampaignId, setReviewingCampaignId] = useState<string | null>(null);
  const [reviewingCampaignName, setReviewingCampaignName] = useState<string>('');

  // Review config selection dialog state
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [pendingReview, setPendingReview] = useState<{
    campaignId: string;
    campaignName: string;
    unreviewedOnly: boolean;
  } | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');

  const { data: reviewConfigs = [] } = useReviewConfigs();
  const { mutate: startReview, isPending: isStartingReview } = useStartReview();

  // Poll for active review status
  const { data: activeReview } = useCampaignReview(activeReviewId || undefined);

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Debounce search input and sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, debouncedSearch, updateParams]);

  // Redirect when review is completed
  useEffect(() => {
    if (activeReview?.status === 'COMPLETED' && reviewingCampaignId) {
      toast({
        title: 'Review Completed',
        description: 'AI review process has been completed successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-reviews', reviewingCampaignId] });

      navigate(`/reviews/campaign/${reviewingCampaignId}/history`);
      // Use setTimeout to avoid setState during render
      setTimeout(() => {
        setActiveReviewId(null);
        setReviewingCampaignId(null);
        setReviewingCampaignName('');
      }, 0);
    } else if (activeReview?.status === 'FAILED') {
      toast({
        title: 'Review Failed',
        description: 'The review process encountered an error.',
        variant: 'destructive',
      });
      // Use setTimeout to avoid setState during render
      setTimeout(() => {
        setActiveReviewId(null);
        setReviewingCampaignId(null);
        setReviewingCampaignName('');
      }, 0);
    }
  }, [activeReview, reviewingCampaignId, navigate, toast, queryClient]);

  // Build query from state - filter on backend
  const query = useMemo(() => {
    const q: Partial<CampaignQuery> = { page, limit, has_calls: true };
    if (debouncedSearch) q.search = debouncedSearch;
    if (reviewFilter) q.has_reviews = reviewFilter as 'reviewed' | 'not_reviewed';
    return q;
  }, [page, limit, debouncedSearch, reviewFilter]);

  const { data, isLoading } = useCampaigns(query);
  const campaigns = data?.data ?? [];
  const pagination = data?.pagination;

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchInput || reviewFilter;

  const handleStartReview = (campaignId: string, campaignName: string, unreviewedOnly: boolean) => {
    setPendingReview({ campaignId, campaignName, unreviewedOnly });
    setSelectedConfigId('');
    setConfigDialogOpen(true);
  };

  const handleConfirmStartReview = () => {
    if (!pendingReview) return;
    setConfigDialogOpen(false);

    startReview(
      {
        campaignId: pendingReview.campaignId,
        unreviewedOnly: pendingReview.unreviewedOnly,
        reviewConfigId: selectedConfigId || undefined,
      },
      {
        onSuccess: (data) => {
          setActiveReviewId(data.reviewId);
          setReviewingCampaignId(pendingReview.campaignId);
          setReviewingCampaignName(pendingReview.campaignName);
          toast({
            title: 'Review Started',
            description: pendingReview.unreviewedOnly
              ? 'AI review process has been initiated for unreviewed calls only. Please wait...'
              : 'AI review process has been initiated for all calls. Please wait...',
          });
          setPendingReview(null);
        },
      },
    );
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">AI Review</p>
        <h1 className="text-3xl font-bold tracking-tight">Campaign Reviews</h1>
        <p className="text-muted-foreground max-w-3xl">
          Review call outcomes and outputs with AI-powered analysis. Select a campaign to start the
          review process or view past reviews.
        </p>
      </header>

      {reviewingCampaignId && activeReview && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">
                  Review in Progress - {reviewingCampaignName}
                </p>
                <p className="text-sm text-blue-700">
                  Reviewing {activeReview.reviewed_calls} of {activeReview.total_calls} calls...
                </p>
              </div>
              <Badge variant="outline" className="border-blue-300 bg-blue-100 text-blue-700">
                {activeReview.status}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Campaigns with Calls</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-50 flex-1">
              <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by campaign name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-37.5">
              <label
                htmlFor="review-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Review Status
              </label>
              <select
                id="review-filter"
                value={reviewFilter}
                onChange={(e) => updateParams({ reviewed: e.target.value || null, page: null })}
                className="focus:ring-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              >
                {REVIEW_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading campaigns...</p>
              </div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No campaigns found.{' '}
              {hasActiveFilters
                ? 'Try adjusting your filters.'
                : 'Only campaigns with completed calls can be reviewed.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2">Campaign Name</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Total Calls</th>
                    <th className="border-b px-3 py-2">Completed Calls</th>
                    <th className="border-b px-3 py-2">Review Status</th>
                    <th className="border-b px-3 py-2">Last Reviewed</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => {
                    const hasReviews =
                      campaign.campaignReviews && campaign.campaignReviews.length > 0;
                    const lastReview = hasReviews ? campaign.campaignReviews![0] : null;
                    return (
                      <tr key={campaign.id} className="align-middle">
                        <td className="border-b px-3 py-2 font-medium">{campaign.name}</td>
                        <td className="border-b px-3 py-2">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_BADGE_STYLES[campaign.status]}`}
                          >
                            {campaign.status}
                          </span>
                        </td>
                        <td className="border-b px-3 py-2">{campaign.callsCount}</td>
                        <td className="border-b px-3 py-2">
                          <span className="font-medium text-emerald-600">
                            {campaign.completedCallsCount}
                          </span>
                        </td>
                        <td className="border-b px-3 py-2">
                          {hasReviews ? (
                            <Badge
                              variant="outline"
                              className="border-emerald-200 bg-emerald-50 text-emerald-700"
                            >
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              Reviewed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-600">
                              Not Reviewed
                            </Badge>
                          )}
                        </td>
                        <td className="text-muted-foreground border-b px-3 py-2">
                          {lastReview
                            ? formatDate(lastReview.completedAt || lastReview.createdAt)
                            : '-'}
                        </td>
                        <td className="border-b px-3 py-2">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/reviews/campaign/${campaign.id}/history`)}
                            >
                              <History className="mr-1.5 h-4 w-4" />
                              History
                            </Button>
                            {/* <Button
                              size="sm"
                              onClick={() => navigate(`/campaigns/${campaign.id}`)}
                              variant="secondary"
                            >
                              <>
                                <Eye className="mr-1.5 h-4 w-4" />
                                View Campaign
                              </>
                            </Button> */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  disabled={
                                    !!reviewingCampaignId ||
                                    isStartingReview ||
                                    campaign.completedCallsCount === 0
                                  }
                                >
                                  {reviewingCampaignId === campaign.id ? (
                                    <>
                                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                      Reviewing...
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle2 className="mr-1.5 h-4 w-4" />
                                      Start Review
                                      <ChevronDown className="ml-1.5 h-4 w-4" />
                                    </>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onSelect={() =>
                                    handleStartReview(campaign.id, campaign.name, false)
                                  }
                                >
                                  Review All Calls
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() =>
                                    handleStartReview(campaign.id, campaign.name, true)
                                  }
                                >
                                  Review Unreviewed Only
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination && campaigns.length > 0 && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="campaigns"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Review Config</DialogTitle>
            <DialogDescription>
              Choose a review configuration to use for this review, or leave empty to use the
              default.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <select
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
              value={selectedConfigId}
              onChange={(e) => setSelectedConfigId(e.target.value)}
            >
              <option value="">Use default config</option>
              {reviewConfigs.map((rc) => (
                <option key={rc.id} value={rc.id}>
                  {rc.name}
                  {rc.is_default ? ' (System Default)' : ''}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmStartReview} disabled={isStartingReview}>
              {isStartingReview ? 'Starting...' : 'Start Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
